import { useCallback, useEffect, useState } from "react"
import { useAuth0 } from "@auth0/auth0-react"
import { useNavigate } from "react-router-dom"
import { Fetching } from "../../components/Fetching"
import { Backdrop, Box, Button, Card, CardContent, CardHeader, CircularProgress, Fade, Grid, IconButton, Input, Menu, MenuItem, Pagination, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, useTheme } from "@mui/material"
import { ChevronRight } from "@mui/icons-material"
import { useGetUserAccounts, useGetUserInvitations, useMutateDeleteUser, useMutateDeleteUserInvitation, useMutateResendInviteUser } from "./hooks"
import { SearchIcon, FilterIconDark } from '../../assets/icons/icons'
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DeleteModal from "../../components/DeleteModal"
import dayjs from "dayjs"
import Excel from 'exceljs'
import moment from "moment"


export const UserManagerNew = () => {
    const { isLoading, user } = useAuth0()
    const theme = useTheme()
    const navigate = useNavigate()

    const { data: userList, isFetching, error, refetch } = useGetUserAccounts('useraccounts')
    const [page, setPage] = useState(1)
    const [openLimit, setOpenLimit] = useState<null | HTMLElement>(null)
    const [limit, setLimit] = useState(10)
    const [totalPages, setTotalPages] = useState(0)
    const [paginatedData, setPaginatedData] = useState([]);

    const { data: userInvitationList } = useGetUserInvitations('useraccounts')
    const [invitePage, setInvitePage] = useState(1)
    const [openInviteLimit, setOpenInviteLimit] = useState<null | HTMLElement>(null)
    const [inviteLimit, setInviteLimit] = useState(10)
    const [totalInvitePages, setTotalInvitePages] = useState(0)
    const [paginatedInviteData, setPaginatedInviteData] = useState([]);


    const handleUploadClick = async (exportType: string) => {
        let workbook = new Excel.Workbook()
        try {
            let worksheet = workbook.addWorksheet('Sheet')
            if (exportType === 'Active Users') {
                worksheet.columns = [
                    { header: 'Name', key: 'name', width: 20 },
                    { header: 'Email', key: 'email', width: 20 },
                    { header: 'User Role', key: 'type', width: 20 },
                ]
                userList.forEach((data: any) => {
                    worksheet.addRow({
                        name: `${data.firstName} ${data.lastName}`,
                        email: data?.email,
                        type: data.type,
                    })
                })
            } else {
                worksheet.columns = [
                    { header: 'Name', key: 'name', width: 20 },
                    { header: 'Email', key: 'inviteeEmail', width: 20 },
                    { header: 'Sent Date', key: 'updatedAt', width: 20 },
                    { header: 'Invited By', key: 'invitor', width: 20 },
                    { header: 'User Role', key: 'roleToAssign', width: 20 },
                ]
                userInvitationList.forEach((data: any) => {
                    worksheet.addRow({
                        name: data.name,
                        inviteeEmail: data?.inviteeEmail,
                        updatedAt: dayjs(data?.updatedAt).format('MM/DD/YYYY hh:mm A'),
                        invitor: data?.invitor,
                        roleToAssign: data.roleToAssign,
                    })
                })
            }
            const buffer = await workbook.csv.writeBuffer()
            const blob = new Blob([buffer], {
                type: 'text/csv',
            })
            const fileName = `${exportType} ${moment().format('DD.MM.YY')}`
            const file = new File([blob], fileName, {
                type: 'text/csv',
            })

            const fileURL = URL.createObjectURL(file)

            const downloadLink = document.createElement('a')
            downloadLink.href = fileURL
            downloadLink.download = `${fileName}.${'csv'}`
            document.body.appendChild(downloadLink)
            downloadLink.click()

        } catch (err) {
            console.error(err)
        }
    }

    const handlePageChange = (value: any, type: string) => {
        if (type === 'userList') {
            setPage(1)
            setLimit(value)
            setOpenLimit(null)
        }
        if (type === 'invitationList') {
            setInvitePage(1)
            setInviteLimit(value);
            setOpenInviteLimit(null)
        }
    }

    useEffect(() => {
        if (userList) {
            const totalPagess = Math.ceil(userList?.length / limit)
            setTotalPages(totalPagess)
            const startIndex = (page - 1) * limit;
            const endIndex = startIndex + limit;
            setPaginatedData(userList.slice(startIndex, endIndex));
        }
    }, [limit, page, totalPages, userList])

    useEffect(() => {
        if (userInvitationList) {
            const totalPagess = Math.ceil(userInvitationList?.length / inviteLimit)
            setTotalInvitePages(totalPagess)
            const startIndex = (invitePage - 1) * inviteLimit;
            const endIndex = startIndex + inviteLimit;
            setPaginatedInviteData(userInvitationList.slice(startIndex, endIndex));
        }
    }, [inviteLimit, invitePage, totalInvitePages, userInvitationList])

    if (isLoading) {
        return <Fetching />
    }

    return (
        <>
            <Box sx={{ mx: 2, mb: 0 }}>
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mb: 2
                }}>
                    <Typography sx={{ fontSize: 36, fontWeight: 500 }}>
                        Users Management
                    </Typography>
                    <Button
                        variant='contained'
                        sx={{
                            padding: theme.spacing(1, 2.5, 1, 2.5),
                            borderRadius: '8px',
                            textTransform: 'capitalize',
                            width: 'auto',
                            fontSize: '13px',
                        }}
                        onClick={() =>
                            navigate('/user-management/new')
                        }
                    > Invite User
                    </Button>
                </Box>
                <Card
                    sx={{
                        padding: 1,
                        boxShadow: 'none',
                        border: '1px solid #EEEEEE',
                        borderRadius: '10px',
                        mb: 3
                    }}>
                    <CardHeader
                        sx={{
                            marginBottom: 0,
                            paddingBottom: 0,
                            paddingTop: '10px !important',
                        }}
                        title={
                            <Box>
                                <Grid container spacing={2} alignItems='center' sx={{ mb: 2 }}>
                                    <Grid item sm={7}>
                                        <Typography sx={{ fontSize: 24, fontWeight: 550 }}>
                                            Active Users
                                        </Typography>
                                        <Typography sx={{ fontSize: 12, fontWeight: 550, color: '#7C7A80' }}>
                                            Manage your team's access with ease. The Active Users dashboard lets you view rolesand adjust permissions quickly,
                                            ensuring your projectsstay secure and efficient.
                                        </Typography>
                                    </Grid>
                                </Grid>
                                <Box>
                                    <Grid container spacing={2} alignItems='center' sx={{ mb: 2 }}>
                                        <Grid item sm={9}>
                                            <Grid container spacing={2} alignItems='center' >
                                                <Grid item sm={7}>
                                                    <Box
                                                        sx={{
                                                            border: '1px solid gray',
                                                            px: 1,
                                                            borderRadius: '8px'
                                                        }}
                                                        display={'flex'}
                                                        alignItems={'center'}
                                                        justifyContent={'center'}>
                                                        <div>
                                                            <SearchIcon />
                                                        </div>
                                                        <Input
                                                            disableUnderline
                                                            value={''}
                                                            name='employeeName'
                                                            sx={{
                                                                width: '100%',
                                                                ml: 1,
                                                                '& input': {
                                                                    fontSize: '13px',
                                                                },
                                                                '& input::placeholder': {
                                                                    fontWeight: 'bold',
                                                                },
                                                            }}
                                                            // onChange={onChangeFormData}
                                                            placeholder='Search Locations'
                                                            inputProps={{ 'aria-label': 'search' }}
                                                        />
                                                    </Box>
                                                </Grid>
                                                <Grid item sm={3}>
                                                    <Button
                                                        variant='contained'
                                                        sx={{
                                                            padding: theme.spacing(1, 2.5, 1, 2.5),
                                                            borderRadius: '8px',
                                                            textTransform: 'capitalize',
                                                            width: 'auto',
                                                            fontSize: '13px',
                                                            backgroundColor: '#EEEEEE',
                                                            color: '#5F5C5F',
                                                            '&:hover': {
                                                                backgroundColor: '#EEEEEE',
                                                                color: '#5F5C5F',
                                                            },
                                                        }}
                                                    // onClick={handleFilter}
                                                    >
                                                        <FilterIconDark /> <Box sx={{ width: 10 }} /> Filter
                                                    </Button>
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                        <Grid item sm={3} sx={{ textAlign: 'end' }}>
                                            <Button
                                                variant='contained'
                                                sx={{
                                                    padding: theme.spacing(1, 2.5, 1, 2.5),
                                                    borderRadius: '8px',
                                                    textTransform: 'capitalize',
                                                    width: 'auto',
                                                    fontSize: '13px',
                                                }}
                                                onClick={(event: any) =>
                                                    handleUploadClick('Active Users')
                                                }> Export
                                            </Button>
                                        </Grid>
                                    </Grid>

                                </Box>
                            </Box>
                        }
                    />
                    <CardContent sx={{ paddingBottom: '15px !important', paddingTop: 0 }}>
                        <UserTable userList={paginatedData} type={'userList'} handlePageChange={handlePageChange}
                            setPage={setPage} setOpenLimit={setOpenLimit} openLimit={openLimit} limit={limit} />

                        <PaginationComponent openLimit={openLimit} setOpenLimit={setOpenLimit} handlePageChange={handlePageChange} totalPages={totalPages} setPage={setPage} limit={limit} type={'userList'} />
                    </CardContent>
                </Card>

                <Card
                    sx={{
                        padding: 1,
                        boxShadow: 'none',
                        border: '1px solid #EEEEEE',
                        borderRadius: '10px',
                    }}>
                    <CardHeader
                        sx={{
                            marginBottom: 0,
                            paddingBottom: 0,
                            paddingTop: '10px !important',
                        }}
                        title={
                            <Box>
                                <Grid container spacing={2} alignItems='center' sx={{ mb: 2 }}>
                                    <Grid item sm={7}>
                                        <Typography sx={{ fontSize: 24, fontWeight: 550 }}>
                                            Pending User Invitation
                                        </Typography>
                                        <Typography sx={{ fontSize: 12, fontWeight: 550, color: '#7C7A80' }}>
                                            Track and manage your pending user invitations effortlessly
                                        </Typography>
                                    </Grid>
                                </Grid>
                                <Box>
                                    <Grid container spacing={2} alignItems='center' sx={{ mb: 2 }}>
                                        <Grid item sm={9}>
                                            <Grid container spacing={2} alignItems='center' >
                                                <Grid item sm={7}>
                                                    <Box
                                                        sx={{
                                                            border: '1px solid gray',
                                                            px: 1,
                                                            borderRadius: '8px'
                                                        }}
                                                        display={'flex'}
                                                        alignItems={'center'}
                                                        justifyContent={'center'}>
                                                        <div>
                                                            <SearchIcon />
                                                        </div>
                                                        <Input
                                                            disableUnderline
                                                            value={''}
                                                            name='employeeName'
                                                            sx={{
                                                                width: '100%',
                                                                ml: 1,
                                                                '& input': {
                                                                    fontSize: '13px',
                                                                },
                                                                '& input::placeholder': {
                                                                    fontWeight: 'bold',
                                                                },
                                                            }}
                                                            // onChange={onChangeFormData}
                                                            placeholder='Search Locations'
                                                            inputProps={{ 'aria-label': 'search' }}
                                                        />
                                                    </Box>
                                                </Grid>
                                                <Grid item sm={3}>
                                                    <Button
                                                        variant='contained'
                                                        sx={{
                                                            padding: theme.spacing(1, 2.5, 1, 2.5),
                                                            borderRadius: '8px',
                                                            textTransform: 'capitalize',
                                                            width: 'auto',
                                                            fontSize: '13px',
                                                            backgroundColor: '#EEEEEE',
                                                            color: '#5F5C5F',
                                                            '&:hover': {
                                                                backgroundColor: '#EEEEEE',
                                                                color: '#5F5C5F',
                                                            },
                                                        }}
                                                    // onClick={handleFilter}
                                                    >
                                                        <FilterIconDark /> <Box sx={{ width: 10 }} /> Filter
                                                    </Button>
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                        <Grid item sm={3} sx={{ textAlign: 'end' }}>
                                            <Button
                                                variant='contained'
                                                sx={{
                                                    padding: theme.spacing(1, 2.5, 1, 2.5),
                                                    borderRadius: '8px',
                                                    textTransform: 'capitalize',
                                                    width: 'auto',
                                                    fontSize: '13px',
                                                }}
                                                onClick={(event: any) =>
                                                    handleUploadClick('Pending Invitations')
                                                }> Export
                                            </Button>
                                        </Grid>
                                    </Grid>

                                </Box>
                            </Box>
                        }
                    />
                    <CardContent sx={{ paddingBottom: '15px !important', paddingTop: 0 }}>
                        <UserTable userList={paginatedInviteData} type={'invitationList'} handlePageChange={handlePageChange}
                            setPage={setInvitePage} setOpenLimit={setOpenInviteLimit} openLimit={openInviteLimit} limit={inviteLimit} />

                        <PaginationComponent openLimit={openInviteLimit} setOpenLimit={setOpenInviteLimit} handlePageChange={handlePageChange} totalPages={totalInvitePages} setPage={setInvitePage} limit={inviteLimit} type={'invitationList'} />

                    </CardContent>
                </Card>
            </Box>
        </>
    )
}

const UserTable = ({ userList, type }: any) => {

    return (
        <>
            <Backdrop
                sx={{ color: '#fff', zIndex: theme => theme.zIndex.drawer + 1 }}
                open={false}>
                <CircularProgress color='inherit' />
            </Backdrop>
            <TableContainer
                component={Paper}
                sx={{ my: 2, boxShadow: 'none' }}>
                {type === 'userList' && <Table
                    sx={{
                        minWidth: 650,
                        boxShadow: 'none',
                    }}
                    aria-label='simple table'>
                    <TableHead>
                        <TableRow
                            sx={{
                                '& th': {
                                    backgroundColor: '#DCDAE1',
                                    padding: '15px',
                                    color: '#5F5C5F',
                                    fontWeight: 550,
                                    whiteSpace: 'nowrap',
                                    border: 'none'
                                },
                                '& th:first-child': {
                                    borderRadius: '10px 0px 0px 10px',
                                },
                                '& th:last-child': {
                                    borderRadius: '0px 10px 10px 0px',
                                },
                            }}>
                            <TableCell>NAME</TableCell>
                            <TableCell>EMAIL</TableCell>
                            <TableCell>USER ROLE</TableCell>
                            <TableCell>ACTIONS</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody
                        sx={{
                            '& td': {
                                backgroundColor: 'white',
                                color: '#5F5C5F',
                                fontWeight: 400,
                                whiteSpace: 'nowrap',
                                padding: '8px',
                                fontSize: 14,
                            },
                            '& td:first-child': {
                                borderRadius: '10px 0px 0px 10px',
                            },
                            '& td:last-child': {
                                borderRadius: '0px 10px 10px 0px',
                            },
                        }}>
                        {(userList || []).slice()?.map((row: any, index: any) => (
                            <TableRow
                                key={row._id}
                                sx={{
                                    '& td, & th': {
                                        border: 0,
                                        background: index % 2 == 0 ? 'white' : '#F1EFF5',
                                    },
                                }}>
                                <TableCell>
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <Box sx={{
                                            height: '40px',
                                            width: '40px',
                                            borderRadius: '50%',
                                            mr: 2
                                        }}>
                                            <img height={'100%'} width={'100%'}
                                                style={{ borderRadius: '50%' }}
                                                src={row?.idPhoto || row?.picture} alt="user profile" />
                                        </Box>
                                        <Typography>{`${row.firstName} ${row.lastName}`}</Typography>
                                    </Box>
                                </TableCell>
                                <TableCell>{row?.email}</TableCell>
                                <TableCell>
                                    <Typography>
                                        <span style={{
                                            background: row?.type == 'vtadmin' ? '#808EB4' : row?.type == 'superintendent' ? '#726988' : '#B17AB2',
                                            padding: '6px 15px',
                                            borderRadius: '8px',
                                            color: '#fff',
                                            fontWeight: 550,
                                            textTransform: 'uppercase',
                                            fontSize: 12,
                                            letterSpacing: 1.3
                                        }}>
                                            {row?.type}
                                        </span>
                                    </Typography>
                                </TableCell>
                                <TableCell>
                                    <UserMenu currentUser={row} type={type} />
                                </TableCell>
                            </TableRow>
                        ))
                        }
                    </TableBody>
                </Table>}

                {type === 'invitationList' && <Table
                    sx={{
                        minWidth: 650,
                        boxShadow: 'none',
                    }}
                    aria-label='simple table'>
                    <TableHead>
                        <TableRow
                            sx={{
                                '& th': {
                                    backgroundColor: '#DCDAE1',
                                    padding: '15px',
                                    color: '#5F5C5F',
                                    fontWeight: 550,
                                    whiteSpace: 'nowrap',
                                    border: 'none'
                                },
                                '& th:first-child': {
                                    borderRadius: '10px 0px 0px 10px',
                                },
                                '& th:last-child': {
                                    borderRadius: '0px 10px 10px 0px',
                                },
                            }}>
                            <TableCell>NAME</TableCell>
                            <TableCell>EMAIL</TableCell>
                            <TableCell>SENT DATE</TableCell>
                            <TableCell>INVITED BY</TableCell>
                            <TableCell>USER ROLE</TableCell>
                            <TableCell>ACTIONS</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody
                        sx={{
                            '& td': {
                                backgroundColor: 'white',
                                color: '#5F5C5F',
                                fontWeight: 400,
                                whiteSpace: 'nowrap',
                                padding: '8px',
                                fontSize: 14,
                            },
                            '& td:first-child': {
                                borderRadius: '10px 0px 0px 10px',
                            },
                            '& td:last-child': {
                                borderRadius: '0px 10px 10px 0px',
                            },
                        }}>
                        {(userList || []).slice()?.map((row: any, index: any) => (
                            <TableRow
                                key={row._id}
                                sx={{
                                    '& td, & th': {
                                        border: 0,
                                        background: index % 2 == 0 ? 'white' : '#F1EFF5',
                                    },
                                }}>
                                <TableCell>{row?.name}</TableCell>
                                <TableCell>{row?.inviteeEmail}</TableCell>
                                <TableCell>{dayjs(row?.updatedAt).format('MM/DD/YYYY hh:mm A')}</TableCell>
                                <TableCell>{row?.invitor}</TableCell>
                                <TableCell>
                                    <Typography>
                                        <span style={{
                                            background: row?.roleToAssign == 'vtadmin' ? '#808EB4' : row?.roleToAssign == 'superintendent' ? '#726988' : '#B17AB2',
                                            padding: '6px 15px',
                                            borderRadius: '8px',
                                            color: '#fff',
                                            fontWeight: 550,
                                            textTransform: 'uppercase',
                                            fontSize: 12,
                                            letterSpacing: 1.3
                                        }}>
                                            {row?.roleToAssign}
                                        </span>
                                    </Typography>
                                </TableCell>
                                <TableCell>
                                    <UserMenu currentUser={row} type={type} />
                                </TableCell>
                            </TableRow>
                        ))
                        }
                    </TableBody>
                </Table>}
            </TableContainer>
        </>
    )
}

const UserMenu = ({ currentUser, type }: any) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
    const menuOpen = Boolean(anchorEl)
    const navigate = useNavigate()
    const [open, setOpen] = useState<boolean>(false)
    const { user, logout } = useAuth0()
    const { mutate: resendInviteMutate } = useMutateResendInviteUser('useraccounts')
    const { mutate: deleteInviteMutate } = useMutateDeleteUserInvitation('useraccounts')
    const { mutate: deleteUserMutate } = useMutateDeleteUser('useraccounts')

    const handleClose = () => {
        setAnchorEl(null)
    }

    const handleResendInvite = () => {
        resendInviteMutate(currentUser.id)
        setAnchorEl(null)
    }

    const deleteAction = useCallback(
        (action: any) => {
            action(currentUser.id, {
                onSuccess: () => {
                    if (user?.email == currentUser?.email) {
                        localStorage.clear()
                        sessionStorage.clear()
                        logout({
                            logoutParams: {
                                returnTo: `${window.location.origin}/login`,
                            },
                        })
                    }
                },
            })
        },
        [deleteInviteMutate, deleteUserMutate]
    )

    return (
        <>
            <Box>
                <Button
                    id="fade-button"
                    aria-controls={menuOpen ? 'fade-menu' : undefined}
                    aria-haspopup="true"
                    aria-expanded={menuOpen ? 'true' : undefined}
                    onClick={(event) => setAnchorEl(event.currentTarget)}
                    sx={{ color: '#5F5C5F' }}
                >
                    <MoreVertIcon />
                </Button>
                {type === 'invitationList' &&
                    <Menu
                        id="fade-menu"
                        MenuListProps={{
                            'aria-labelledby': 'fade-button',
                        }}
                        anchorEl={anchorEl}
                        open={menuOpen}
                        onClose={handleClose}
                        TransitionComponent={Fade}
                    >
                        <MenuItem onClick={handleResendInvite}>Resend Invite</MenuItem>
                        <MenuItem onClick={() => { setOpen(true); handleClose() }}>Delete Invite</MenuItem>
                    </Menu>
                }
                {type === 'userList' &&
                    <Menu
                        id="fade-menu"
                        MenuListProps={{
                            'aria-labelledby': 'fade-button',
                        }}
                        anchorEl={anchorEl}
                        open={menuOpen}
                        onClose={handleClose}
                        TransitionComponent={Fade}
                    >
                        <MenuItem onClick={() => { navigate(`/user-management/${currentUser.id}`); handleClose() }}>View</MenuItem>
                        <MenuItem onClick={() => { setOpen(true); handleClose() }}>Delete</MenuItem>

                    </Menu>
                }
            </Box >
            <DeleteModal
                open={open}
                setOpen={setOpen}
                deleteAction={() => deleteAction(type === 'invitationList' ? deleteInviteMutate : deleteUserMutate)}
            />
        </>
    )
}

const PaginationComponent = ({ openLimit, setOpenLimit, handlePageChange, totalPages, setPage, limit, type }: any) => {

    return (
        <Box
            alignItems={'center'}
            display={'flex'}
            justifyContent={'space-between'}
            mt={1}>

            <Button
                id='basic-button-1'
                aria-controls={Boolean(openLimit) ? 'basic-menu-1' : undefined}
                aria-haspopup='true'
                aria-expanded={Boolean(openLimit) ? 'true' : undefined}
                onClick={(event: any) => setOpenLimit(event.currentTarget)}
                sx={{
                    border: '1px solid #DEE2E0',
                    color: 'black',
                    fontWeight: '400',
                    padding: '5px',
                    borderRadius: '10px',
                }}
                endIcon={
                    <ChevronRight
                        sx={{ transform: 'rotate(90deg)', color: '#5A525B' }}
                    />
                }>
                {limit}
            </Button>
            <Menu
                id='basic-menu-1'
                anchorEl={openLimit}
                open={Boolean(openLimit)}
                onClose={() => setOpenLimit(null)}
                MenuListProps={{
                    'aria-labelledby': 'basic-button-1',
                }}
                sx={{
                    boxShadow: 'none',
                    '& .MuiPaper-root': {
                        boxShadow:
                            '0px 5px 12px 0px #0000000A, 0px 22px 22px 0px #00000008, 0px 49px 30px 0px #00000005, 0px 88px 35px 0px #00000003, 0px 137px 38px 0px #00000000',
                        borderRadius: '10px',
                        border: '1px solid #F1EFF5',
                    },
                    '& ul': {
                        padding: '7px 0px',
                        // backgroundColor: 'red',
                        boxShadow: 'none',
                    },
                    '& li:hover': {
                        backgroundColor: 'transparent',
                    },
                    '& li': {
                        margin: '5px 10px',
                        borderBottom: '1px solid #DEE2E0',
                        textAlign: 'left',
                        padding: '5px 0',
                        fontSize: 14,
                        color: '#5F5C5F',
                    },
                    '& li:last-child': {
                        border: 'none !important',
                    },
                    marginTop: 1,
                }}>
                {[10, 20, 30].map(item => (
                    <MenuItem
                        sx={{
                            width: 48,
                            textAlign: 'center',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                        onClick={() => {
                            handlePageChange(item, type)
                        }}>
                        {item}
                    </MenuItem>
                ))}
            </Menu>

            <Pagination
                count={totalPages || 0}
                onChange={(event, page) => setPage(page)}
                color='primary'
            />
        </Box>
    )
}